import {
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ButtonModule } from 'primeng/button';
import { Subscription } from 'rxjs';

import { CpfInformComponent } from '../../components/cpf-inform/cpf-inform.component';
import { CredentialsListComponent } from '../../components/credentials-list/credentials-list.component';
import { ToolsComponent } from '../../components/tools/tools.component';
import { RecipientCredentials } from '../../types/recipient-credentials.response.type';
import { CredentialRequest } from '../../types/credencial-request.type';
import { AddressStatus } from '../../model/AddressStatus';
import { RecipientCredentialsPaged } from '../../services/people.service';

import { CredentialsStateService } from '../../services/credentials-state.service';
import { PeopleService } from '../../services/people.service';

@Component({
  selector: 'app-emission',
  standalone: true,
  imports: [
    CpfInformComponent,
    FormsModule,
    ButtonModule,
    CredentialsListComponent,
    ToolsComponent,
    CardModule,
    ProgressSpinnerModule,
  ],
  template: `
    <p-card>
      <ng-template pTemplate="header">
        <div class="card-header">
          <h2><i class="pi pi-id-card"></i> Emissão de Credencial</h2>
        </div>
      </ng-template>
      
      @if (isAdmin() || isSuperUser() || isUser()) {
      <main class="principal">
        <div class="container">
          <div class="item_person">
            <app-cpf-inform
              (emissorRecipientList)="receberValorRecipientList($event)"
              (emissorCredencialsEnvolveds)="
                receberValorCredentialsEnvolveds($event)
              "
              [valorToggle]="inputToggle"
              (emissorAddressStatus)="receberValorAddressStatus($event)"
            ></app-cpf-inform>
          </div>
          <div class="item_tools">
            <app-tools
              (emissorToggle)="receberValorToggle($event)"
              [valorCredentialsEnvolveds]="inputRequestBody"
              [valorAddressStatus]="inputAddressStatus"
            ></app-tools>
          </div>
        </div>

        @if(isLoading) {
        <div class="loading-message">
          <p-progressSpinner [style]="{'width': '30px', 'height': '30px'}" strokeWidth="4"></p-progressSpinner>
          <span>Carregando credenciais...</span>
        </div>
        } @else if(input && input.length > 0) {

        <app-credentials-list [valor]="input"></app-credentials-list>

        } @else if(currentRecipientId) {
        <div class="no-credentials">
          Nenhuma credencial encontrada para este beneficiário.
        </div>
        }
      </main>
      } @else {
      <div class="empty-page">
        <p></p>
      </div>
      }
    </p-card>
  `,
  styles: `
    /* PrimeNG Card Customization */
    :host ::ng-deep p-card {
      width: 100%;
    }

    :host ::ng-deep .p-card {
      margin: 0;
      padding: 10px;
      background: white;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      border: none;
    }

    :host ::ng-deep .p-card .p-card-body {
      padding: 10px !important;
      margin: 0;
    }

    .card-header {
      padding: 16px 16px 0;
      margin: 0;
    }

    .card-header h2 {
      color: #325565;
      font-weight: 600;
      font-size: 1.5rem;
      margin: 0;
      font-family: "Montserrat", sans-serif;
    }

    div.principal {
      min-width: 150px;
      width: 100%;
      padding: 0 !important;
      margin: 0;
      box-sizing: border-box;
    }

    div.container {
      display: flex;
      flex-direction: row;
      width: 100%;
      padding: 0 8px !important;
      margin: 0;
    }

    .loading-message {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      gap: 10px;
      margin: 0 8px;
      color: #325565;
      font-weight: 500;
    }

    .loading-message span {
      color: #666;
    }

    .no-credentials {
      text-align: center;
      padding: 20px;
      color: #666;
      font-style: italic;
      margin: 0 8px;
      background: rgba(255, 230, 203, 0.1);
      border-radius: 8px;
      border: 1px solid #FFE6CB;
    }

    app-credentials-list {
      display: block;
      width: 100%;
      margin: 0;
      padding: 0;
    }

    div.item_person {
      width: 85%;
      padding: 0;
      margin: 0;
    }

    div.item_tools {
      width: 15%;
      padding: 0;
      margin: 0;
    }

    .empty-page {
      background: rgba(255, 230, 203, 0.1);
      border: 1px solid #FFE6CB;
      border-radius: 8px;
      padding: 20px;
      text-align: center;
    }

    /* PrimeNG Progress Spinner Customization */
    :host ::ng-deep .p-progress-spinner-circle {
      stroke: #F15A22;
    }

    /* Estilos para telas pequenas (responsivo) */
    @media (max-width: 768px) {
      div.container {
        flex-direction: column;
      }

      div.item_person,
      div.item_tools {
        width: 100%;
      }

      div.item_tools {
        order: 2;
      }

      .card-header {
        padding: 12px 12px 0;
      }

      .card-header h2 {
        font-size: 1.25rem;
      }
    }

    /* Para telas muito grandes - maximizar o uso do espaço */
    @media (min-width: 1200px) {
      :host ::ng-deep .p-card {
        max-width: 100vw;
      }

      div.principal {
        max-width: 100%;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmissionComponent implements OnInit, OnDestroy {
  private createdSubscription: Subscription = new Subscription();
  private cancelledSubscription: Subscription = new Subscription();

  input: RecipientCredentials[] | undefined;
  inputPaged: RecipientCredentialsPaged[] | undefined;
  inputToggle: string | undefined;
  inputRequestBody: CredentialRequest | undefined;
  inputAddressStatus: AddressStatus | undefined;

  isLoading: boolean = false;
  currentApplicantId: number | null = null;
  currentRecipientId: number | null = null;

  constructor(
    private credentialsStateService: CredentialsStateService,
    private peopleService: PeopleService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    // Escuta criação de credenciais
    this.createdSubscription =
      this.credentialsStateService.credentialCreated$.subscribe(() => {
        // console.log('Evento de credencial criada recebido!');
        this.refreshCredentialsList();
      });

    // Escuta cancelamento de credenciais ← ADICIONE ESTE BLOCO
    this.cancelledSubscription =
      this.credentialsStateService.credentialCancelled$.subscribe(
        (cancelled) => {
          if (cancelled) {
            // console.log('Evento de credencial cancelada recebido!');
            this.refreshCredentialsList();
          }
        }
      );
  }

  ngOnDestroy() {
    if (this.createdSubscription) {
      this.createdSubscription.unsubscribe();
    }
    if (this.cancelledSubscription) {
      // ← Adicione esta limpeza
      this.cancelledSubscription.unsubscribe();
    }
  }

  // emission.component.ts
  refreshCredentialsList(): void {
    if (this.currentRecipientId) {
      this.isLoading = true;

      // Primeiro, recarregue os dados completos da pessoa
      this.peopleService.getPeopleById(this.currentRecipientId).subscribe({
        next: (peopleResponse) => {
          // Atualize a lista de credenciais com os dados mais recentes
          this.input = peopleResponse.data.recipientCredentials;
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Erro ao recarregar dados da pessoa:', err);
          this.isLoading = false;
        },
      });
    }

  }

  receberValorRecipientList(resultado: RecipientCredentials[]) {
    this.input = resultado;
    if (resultado.length > 0) {
      this.currentApplicantId = Number(resultado[0].applicantSummary.id);
      this.currentRecipientId = Number(resultado[0].recipientSummary.id);
    }
  }
  /*
  receberValorRecipientPaged(resultadoPaged: RecipientCredentialsPaged[]) {
    this.inputPaged = resultadoPaged;
  }
*/

  receberValorToggle(resultadoToggle: string): void {
    this.inputToggle = resultadoToggle;
  }

  receberValorCredentialsEnvolveds(
    resultadoCredentialsEnvolveds: CredentialRequest
  ) {
    // console.log('CredentialRequest recebido:', resultadoCredentialsEnvolveds); // Debug
    this.inputRequestBody = resultadoCredentialsEnvolveds;
    this.currentApplicantId = resultadoCredentialsEnvolveds.applicantId;
    this.currentRecipientId = resultadoCredentialsEnvolveds.recipientId;
    // console.log('currentRecipientId definido como:', this.currentRecipientId); // Debug
  }

  receberValorAddressStatus(resultadoAddressStatus: AddressStatus) {
    this.inputAddressStatus = resultadoAddressStatus;
  }

  isAdmin(): boolean {
    const roles = JSON.parse(sessionStorage.getItem('roles') || '[]');
    return roles.includes('ROLE_ADMIN');
  }

  isSuperUser(): boolean {
    const roles = JSON.parse(sessionStorage.getItem('roles') || '[]');
    return roles.includes('ROLE_SUPERUSER');
  }

  isUser(): boolean {
    const roles = JSON.parse(sessionStorage.getItem('roles') || '[]');
    return roles.includes('ROLE_USER');
  }
}
